/**
 * Easy-mode living meadow: atmospheric spring sunrise.
 * Soft layered motion via p5 Perlin noise + gentle mouse parallax.
 * Decorative only — never affects gameplay.
 */

// Soft, desaturated meadow palette
const SAGE = [
  [120, 148, 110],
  [98, 130, 95],
  [140, 160, 120],
  [110, 140, 105],
];
const BLOOM = [
  [220, 180, 175], // pale rose
  [235, 210, 170], // cream gold
  [200, 195, 210], // soft lilac
  [230, 200, 185], // peach mist
  [190, 205, 195], // sage bloom
];
const PETAL = [
  [235, 200, 195],
  [245, 225, 190],
  [215, 205, 220],
];

function rand(a, b) {
  return a + Math.random() * (b - a);
}

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function n01(p, x, y, z) {
  // p5 noise is 0..1
  return p.noise(x, y, z);
}

function sway(p, seed, t, amp, speed) {
  const n = n01(p, seed * 0.17, t * speed, seed * 0.03);
  return (n - 0.5) * 2 * amp;
}

function parallax(mx, my, depth, boardW, boardH) {
  // depth 0 (far) → almost still; depth 1 (near) → more response
  const nx = (mx / Math.max(1, boardW) - 0.5) * 2;
  const ny = (my / Math.max(1, boardH) - 0.5) * 2;
  return {
    x: nx * depth * 14,
    y: ny * depth * 8,
  };
}

export function clearMeadow(state) {
  state.meadow = null;
}

export function seedMeadow(state, boardW, boardH) {
  const grasses = [];
  // Distant soft stalks
  for (let i = 0; i < 18; i += 1) {
    grasses.push({
      x: rand(0.02, 0.98),
      baseY: rand(0.72, 0.86),
      h: rand(0.08, 0.16),
      depth: rand(0.15, 0.35),
      seed: rand(0, 100),
      amp: rand(2, 5),
      speed: rand(0.0006, 0.0012),
      color: pick(SAGE),
      thick: rand(1.1, 1.8),
    });
  }
  // Mid grass
  for (let i = 0; i < 28; i += 1) {
    grasses.push({
      x: rand(0.01, 0.99),
      baseY: rand(0.78, 0.94),
      h: rand(0.12, 0.24),
      depth: rand(0.4, 0.7),
      seed: rand(0, 100),
      amp: rand(4, 9),
      speed: rand(0.0008, 0.0016),
      color: pick(SAGE),
      thick: rand(1.4, 2.4),
    });
  }
  // Foreground grass
  for (let i = 0; i < 22; i += 1) {
    grasses.push({
      x: rand(0, 1),
      baseY: rand(0.88, 1.02),
      h: rand(0.16, 0.32),
      depth: rand(0.75, 1),
      seed: rand(0, 100),
      amp: rand(7, 14),
      speed: rand(0.001, 0.0022),
      color: pick(SAGE),
      thick: rand(1.8, 3.2),
    });
  }

  const blooms = [];
  // Distant meadow flecks
  for (let i = 0; i < 16; i += 1) {
    blooms.push({
      x: rand(0.05, 0.95),
      y: rand(0.55, 0.78),
      r: rand(2.5, 5),
      depth: rand(0.12, 0.35),
      seed: rand(0, 100),
      amp: rand(1.5, 3.5),
      speed: rand(0.0005, 0.001),
      color: pick(BLOOM),
      alpha: rand(70, 120),
      petals: 4 + Math.floor(Math.random() * 2),
    });
  }
  // Mid flowers
  for (let i = 0; i < 14; i += 1) {
    blooms.push({
      x: rand(0.04, 0.96),
      y: rand(0.62, 0.88),
      r: rand(4, 7.5),
      depth: rand(0.4, 0.7),
      seed: rand(0, 100),
      amp: rand(3, 7),
      speed: rand(0.0007, 0.0014),
      color: pick(BLOOM),
      alpha: rand(110, 165),
      petals: 5,
    });
  }
  // Foreground soft blooms
  for (let i = 0; i < 10; i += 1) {
    blooms.push({
      x: rand(0.02, 0.98),
      y: rand(0.72, 0.96),
      r: rand(5.5, 9),
      depth: rand(0.75, 1),
      seed: rand(0, 100),
      amp: rand(5, 11),
      speed: rand(0.0009, 0.0018),
      color: pick(BLOOM),
      alpha: rand(130, 185),
      petals: 5 + Math.floor(Math.random() * 2),
    });
  }

  const particles = [];
  for (let i = 0; i < 28; i += 1) {
    particles.push({
      kind: Math.random() > 0.55 ? 'pollen' : 'dust',
      x: rand(0, 1),
      y: rand(0.05, 0.95),
      r: rand(1, 2.8),
      depth: rand(0.3, 0.95),
      seed: rand(0, 100),
      drift: rand(0.00015, 0.00045),
      alpha: rand(40, 95),
    });
  }
  for (let i = 0; i < 10; i += 1) {
    particles.push({
      kind: 'petal',
      x: rand(0, 1),
      y: rand(0.1, 0.85),
      r: rand(3, 6),
      depth: rand(0.45, 1),
      seed: rand(0, 100),
      drift: rand(0.0002, 0.0005),
      alpha: rand(70, 130),
      color: pick(PETAL),
      spin: rand(0, Math.PI * 2),
    });
  }

  const haze = [];
  for (let i = 0; i < 5; i += 1) {
    haze.push({
      x: rand(0.1, 0.9),
      y: rand(0.08, 0.45),
      w: rand(0.22, 0.42),
      h: rand(0.08, 0.18),
      depth: rand(0.05, 0.25),
      seed: rand(0, 100),
      alpha: rand(18, 38),
    });
  }

  const rays = [];
  for (let i = 0; i < 6; i += 1) {
    rays.push({
      angle: rand(-0.35, 0.55),
      width: rand(0.04, 0.1),
      alpha: rand(10, 22),
      seed: rand(0, 100),
      length: rand(0.7, 1.15),
    });
  }

  const iridescence = [];
  for (let i = 0; i < 4; i += 1) {
    iridescence.push({
      x: rand(0.2, 0.85),
      y: rand(0.12, 0.55),
      w: rand(0.18, 0.35),
      h: rand(0.06, 0.14),
      seed: rand(0, 100),
      hueShift: rand(0, 1),
      lifePhase: rand(0, Math.PI * 2),
    });
  }

  grasses.sort((a, b) => a.depth - b.depth);
  blooms.sort((a, b) => a.depth - b.depth);

  state.meadow = {
    w: boardW,
    h: boardH,
    grasses,
    blooms,
    particles,
    haze,
    rays,
    iridescence,
    time: 0,
  };
}

function drawSky(p, boardW, boardH, t, reducedMotion, px, py) {
  p.push();
  p.noStroke();
  // Warm sunrise sky
  for (let i = 0; i < 8; i += 1) {
    const y0 = (boardH * i) / 8;
    const y1 = (boardH * (i + 1)) / 8 + 1;
    const k = i / 7;
    const r = 255 - k * 35;
    const g = 236 - k * 20;
    const b = 210 + k * 25;
    p.fill(r, g, b, 55);
    p.rect(0, y0, boardW, y1 - y0);
  }
  // Golden sun bloom (soft, no hard disc)
  const breath = reducedMotion ? 0 : (n01(p, t * 0.0004, 1.2, 2.4) - 0.5) * 12;
  const sx = boardW * 0.14 + px * 0.3 + breath;
  const sy = boardH * 0.18 + py * 0.2;
  p.fill(255, 220, 150, 28);
  p.ellipse(sx, sy, boardH * 0.55, boardH * 0.45);
  p.fill(255, 230, 170, 40);
  p.ellipse(sx, sy, boardH * 0.28, boardH * 0.24);
  p.fill(255, 245, 210, 55);
  p.ellipse(sx, sy, boardH * 0.12, boardH * 0.11);
  p.pop();
  return { sx, sy };
}

function drawDistantHills(p, boardW, boardH, t, reducedMotion, px, py) {
  p.push();
  p.noStroke();
  const drift = reducedMotion ? 0 : sway(p, 3.3, t, 6, 0.00035) + px * 0.15;
  p.fill(170, 185, 155, 45);
  p.beginShape();
  p.vertex(-20 + drift, boardH);
  for (let i = 0; i <= 12; i += 1) {
    const x = (boardW * i) / 12 + drift;
    const y =
      boardH * 0.62 +
      n01(p, i * 0.35, t * 0.0002, 8) * boardH * 0.08 +
      py * 0.1;
    p.vertex(x, y);
  }
  p.vertex(boardW + 20 + drift, boardH);
  p.endShape(p.CLOSE);

  p.fill(150, 170, 140, 50);
  const drift2 = reducedMotion ? 0 : sway(p, 7.1, t, 8, 0.0004) + px * 0.25;
  p.beginShape();
  p.vertex(-20 + drift2, boardH);
  for (let i = 0; i <= 14; i += 1) {
    const x = (boardW * i) / 14 + drift2;
    const y =
      boardH * 0.7 +
      n01(p, i * 0.4 + 2, t * 0.00025, 9) * boardH * 0.1 +
      py * 0.15;
    p.vertex(x, y);
  }
  p.vertex(boardW + 20 + drift2, boardH);
  p.endShape(p.CLOSE);
  p.pop();
}

function drawHaze(p, meadow, boardW, boardH, t, reducedMotion, mx, my) {
  p.push();
  p.noStroke();
  for (let i = 0; i < meadow.haze.length; i += 1) {
    const h = meadow.haze[i];
    const par = parallax(mx, my, h.depth, boardW, boardH);
    const dx = reducedMotion ? 0 : sway(p, h.seed, t, 10, 0.0004);
    const dy = reducedMotion ? 0 : sway(p, h.seed + 1, t, 5, 0.00035);
    p.fill(255, 248, 235, h.alpha);
    p.ellipse(
      boardW * h.x + dx + par.x,
      boardH * h.y + dy + par.y,
      boardW * h.w,
      boardH * h.h
    );
  }
  p.pop();
}

function drawRays(p, meadow, sun, boardW, boardH, t, reducedMotion) {
  p.push();
  p.noStroke();
  for (let i = 0; i < meadow.rays.length; i += 1) {
    const ray = meadow.rays[i];
    const pulse = reducedMotion
      ? 1
      : 0.75 + n01(p, ray.seed, t * 0.0005, 4) * 0.5;
    const a = Math.floor(ray.alpha * pulse);
    const ang = ray.angle + (reducedMotion ? 0 : sway(p, ray.seed, t, 0.04, 0.0003));
    const len = boardH * ray.length;
    const half = boardW * ray.width * 0.5;
    p.push();
    p.translate(sun.sx, sun.sy);
    p.rotate(ang);
    p.fill(255, 235, 180, a);
    p.beginShape();
    p.vertex(0, 0);
    p.vertex(half, len);
    p.vertex(-half, len);
    p.endShape(p.CLOSE);
    p.pop();
  }
  p.pop();
}

function drawIridescence(p, meadow, boardW, boardH, t, reducedMotion, mx, my) {
  const bands = [
    [255, 190, 190],
    [255, 220, 170],
    [200, 220, 190],
    [190, 210, 230],
    [220, 200, 230],
  ];
  p.push();
  p.noStroke();
  for (let i = 0; i < meadow.iridescence.length; i += 1) {
    const ir = meadow.iridescence[i];
    const life = reducedMotion
      ? 0.45
      : 0.25 +
        0.55 *
          (0.5 +
            0.5 *
              Math.sin(t * 0.0011 + ir.lifePhase) *
              (0.6 + n01(p, ir.seed, t * 0.0004, 5) * 0.4));
    if (life < 0.08) continue;
    const par = parallax(mx, my, 0.35, boardW, boardH);
    const dx = reducedMotion ? 0 : sway(p, ir.seed, t, 18, 0.00035);
    const dy = reducedMotion ? 0 : sway(p, ir.seed + 2, t, 10, 0.0003);
    const cx = boardW * ir.x + dx + par.x;
    const cy = boardH * ir.y + dy + par.y;
    for (let b = 0; b < bands.length; b += 1) {
      const [r, g, bl] = bands[b];
      p.fill(r, g, bl, Math.floor(16 * life));
      p.ellipse(
        cx + (b - 2) * boardW * 0.012,
        cy,
        boardW * ir.w * (1 - b * 0.08),
        boardH * ir.h * (1 - b * 0.05)
      );
    }
  }
  p.pop();
}

function drawGrassBlade(p, g, boardW, boardH, t, reducedMotion, mx, my) {
  const par = parallax(mx, my, g.depth, boardW, boardH);
  const baseX = boardW * g.x + par.x;
  const baseY = boardH * g.baseY + par.y * 0.4;
  const h = boardH * g.h;
  const tipSway = reducedMotion ? 0 : sway(p, g.seed, t, g.amp, g.speed);
  const midSway = tipSway * 0.45;
  const [r, gr, b] = g.color;
  const alpha = Math.floor(70 + g.depth * 90);

  p.push();
  p.noFill();
  p.stroke(r, gr, b, alpha);
  p.strokeWeight(g.thick);
  p.bezier(
    baseX,
    baseY,
    baseX + midSway * 0.3,
    baseY - h * 0.4,
    baseX + midSway,
    baseY - h * 0.75,
    baseX + tipSway,
    baseY - h
  );
  p.pop();
}

function drawBloom(p, f, boardW, boardH, t, reducedMotion, mx, my) {
  const par = parallax(mx, my, f.depth, boardW, boardH);
  const swayX = reducedMotion ? 0 : sway(p, f.seed, t, f.amp, f.speed);
  const swayY = reducedMotion ? 0 : sway(p, f.seed + 3, t, f.amp * 0.35, f.speed * 0.9);
  const x = boardW * f.x + swayX + par.x;
  const y = boardH * f.y + swayY + par.y;
  const [r, g, b] = f.color;
  const a = Math.floor(f.alpha);

  p.push();
  p.noStroke();
  // soft stem hint for nearer blooms
  if (f.depth > 0.45) {
    p.stroke(110, 140, 100, Math.floor(a * 0.35));
    p.strokeWeight(1.2);
    p.line(x - swayX * 0.2, y + f.r * 1.6, x, y);
    p.noStroke();
  }
  for (let i = 0; i < f.petals; i += 1) {
    const ang = (Math.PI * 2 * i) / f.petals + f.seed;
    p.fill(r, g, b, a);
    p.circle(
      x + Math.cos(ang) * f.r * 0.55,
      y + Math.sin(ang) * f.r * 0.55,
      f.r
    );
  }
  p.fill(245, 230, 180, Math.floor(a * 0.85));
  p.circle(x, y, f.r * 0.7);
  p.pop();
}

function drawParticles(p, meadow, boardW, boardH, t, reducedMotion, mx, my) {
  p.push();
  p.noStroke();
  for (let i = 0; i < meadow.particles.length; i += 1) {
    const pt = meadow.particles[i];
    if (!reducedMotion) {
      const nx = n01(p, pt.seed, t * pt.drift, 1.1);
      const ny = n01(p, pt.seed + 5, t * pt.drift * 0.85, 2.2);
      pt.x += (nx - 0.48) * 0.0009 * (0.5 + pt.depth);
      pt.y += (ny - 0.52) * 0.0007 + 0.00005;
      if (pt.x < -0.05) pt.x = 1.05;
      if (pt.x > 1.05) pt.x = -0.05;
      if (pt.y < -0.05) pt.y = 1.05;
      if (pt.y > 1.05) pt.y = -0.05;
      if (pt.kind === 'petal') pt.spin += 0.008 + pt.depth * 0.01;
    }
    const par = parallax(mx, my, pt.depth, boardW, boardH);
    const x =
      boardW * pt.x +
      par.x +
      (reducedMotion ? 0 : sway(p, pt.seed, t, 4 + pt.depth * 6, 0.0007));
    const y =
      boardH * pt.y +
      par.y +
      (reducedMotion ? 0 : sway(p, pt.seed + 1, t, 3, 0.0006));

    if (pt.kind === 'petal') {
      const [r, g, b] = pt.color;
      p.push();
      p.translate(x, y);
      p.rotate(pt.spin || 0);
      p.fill(r, g, b, pt.alpha);
      p.ellipse(0, 0, pt.r * 1.6, pt.r * 0.7);
      p.pop();
    } else {
      p.fill(255, 245, 220, pt.alpha);
      p.circle(x, y, pt.r);
    }
  }
  p.pop();
}

/**
 * Draw the full meadow stack. Call only for sunshine weather.
 */
export function drawMeadow(p, state, boardW, boardH, t, reducedMotion, mouseX, mouseY) {
  if (!state.meadow) return;
  const meadow = state.meadow;

  // Re-seed if the board changed a lot (resize)
  if (
    Math.abs(meadow.w - boardW) > 48 ||
    Math.abs(meadow.h - boardH) > 36
  ) {
    seedMeadow(state, boardW, boardH);
  }

  const mx =
    mouseX == null || mouseX < 0 || mouseX > boardW ? boardW * 0.5 : mouseX;
  const my =
    mouseY == null || mouseY < 0 || mouseY > boardH ? boardH * 0.4 : mouseY;
  const farPar = parallax(mx, my, 0.12, boardW, boardH);

  const sun = drawSky(p, boardW, boardH, t, reducedMotion, farPar.x, farPar.y);
  drawDistantHills(p, boardW, boardH, t, reducedMotion, farPar.x, farPar.y);
  drawHaze(p, meadow, boardW, boardH, t, reducedMotion, mx, my);
  drawRays(p, meadow, sun, boardW, boardH, t, reducedMotion);
  drawIridescence(p, meadow, boardW, boardH, t, reducedMotion, mx, my);

  // Flora is pre-sorted far → near; draw in depth bands for layering
  const grasses = meadow.grasses;
  const blooms = meadow.blooms;

  for (let i = 0; i < grasses.length; i += 1) {
    if (grasses[i].depth < 0.55) {
      drawGrassBlade(p, grasses[i], boardW, boardH, t, reducedMotion, mx, my);
    }
  }
  for (let i = 0; i < blooms.length; i += 1) {
    if (blooms[i].depth < 0.55) {
      drawBloom(p, blooms[i], boardW, boardH, t, reducedMotion, mx, my);
    }
  }
  for (let i = 0; i < grasses.length; i += 1) {
    if (grasses[i].depth >= 0.55 && grasses[i].depth < 0.8) {
      drawGrassBlade(p, grasses[i], boardW, boardH, t, reducedMotion, mx, my);
    }
  }
  for (let i = 0; i < blooms.length; i += 1) {
    if (blooms[i].depth >= 0.55 && blooms[i].depth < 0.8) {
      drawBloom(p, blooms[i], boardW, boardH, t, reducedMotion, mx, my);
    }
  }

  drawParticles(p, meadow, boardW, boardH, t, reducedMotion, mx, my);

  for (let i = 0; i < grasses.length; i += 1) {
    if (grasses[i].depth >= 0.8) {
      drawGrassBlade(p, grasses[i], boardW, boardH, t, reducedMotion, mx, my);
    }
  }
  for (let i = 0; i < blooms.length; i += 1) {
    if (blooms[i].depth >= 0.8) {
      drawBloom(p, blooms[i], boardW, boardH, t, reducedMotion, mx, my);
    }
  }
}
